<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import gettext from '@/i18n/gettext';

import {
  type AdvancedSettings,
  PART_SYMMETRY_ROTATION_NOT_REQUIRED,
} from '@/models/RobVision';
import { requiredRule } from '@/helpers/FormRules';

const { $gettext } = gettext;
const props = defineProps<{
  showCADOptions: boolean;
  advancedSettings: AdvancedSettings;
  isGlbFile: boolean;
}>();

const { advancedSettings } = toRefs(props);

// Last non-zero symmetry, restored when "rotation not required" is unticked again.
const lastPartSymmetry = ref(advancedSettings.value.partSymmetry || 360);

const rotationNotRequired = computed({
  get: () =>
    advancedSettings.value.partSymmetry === PART_SYMMETRY_ROTATION_NOT_REQUIRED,
  set: (notRequired: boolean) => {
    if (notRequired) {
      lastPartSymmetry.value = advancedSettings.value.partSymmetry;
      advancedSettings.value.partSymmetry = PART_SYMMETRY_ROTATION_NOT_REQUIRED;
    } else {
      advancedSettings.value.partSymmetry = lastPartSymmetry.value || 360;
    }
  },
});

const partSymmetryRules = [
  requiredRule,
  (v: number) => (v >= 1 && v <= 360) || $gettext('Must be between 1 and 360'),
  (v: number) => 360 % v == 0 || $gettext('Must be a divisor of 360'),
];
</script>

<template>
  <div v-if="showCADOptions">
    <v-row>
      <v-col cols="12" md="6">
        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-checkbox
              v-bind="props"
              v-model="advancedSettings.ropeFlip"
              :label="$gettext('Activate flip')"
              density="comfortable"
              hide-details
            />
          </template>
          <span>{{
            $gettext('Trains rotation model with backside as well.')
          }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-checkbox
              v-bind="props"
              v-model="advancedSettings.keepTextures"
              :label="$gettext('Keep Texture')"
              density="comfortable"
              :disabled="!isGlbFile"
              hide-details
            />
          </template>
          <span>{{
            $gettext(
              'Keeps the original texture instead of overriding with the chosen texture.',
            )
          }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
  </div>

  <v-row>
    <v-col cols="12" md="6">
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-text-field
            v-bind="props"
            v-model.number="advancedSettings.partSymmetry"
            :label="$gettext('Part Symmetry')"
            type="number"
            variant="filled"
            density="comfortable"
            suffix="°"
            step="1"
            :disabled="rotationNotRequired"
            :rules="rotationNotRequired ? [] : partSymmetryRules"
            class="w-100"
          />
        </template>
        <span>{{
          $gettext(
            'Enter the rotational symmetry of the part in degrees. 360 means no symmetry, 180 means 2-fold symmetry, etc. Must divide 360.',
          )
        }}</span>
      </v-tooltip>
    </v-col>
    <v-col cols="12" md="6">
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-checkbox
            v-bind="props"
            v-model="rotationNotRequired"
            :label="$gettext('Rotation not required')"
            density="comfortable"
            hide-details
          />
        </template>
        <span>{{
          $gettext(
            'The part orientation does not matter for picking (e.g. round parts). No rotation is trained.',
          )
        }}</span>
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<style scoped>
.equal-height-row .v-col {
  display: flex;
}

.equal-height-row .v-card {
  height: 100%;
}
</style>
